import { CheckCircle2, Pencil, Printer, Trash2, Undo2 } from "lucide-react";
import Swal from "sweetalert2";
import type { ProformaInvoice } from "./index";
import { canSalesExpertModify } from "./index";
import { getOrderColumns, tableButtonClass } from "Common/Components/orderColumns";
import { isSettled } from "Common/constants/proformaStatus";
import { ltrValue } from "Common/constants/panelSpec";

export interface ProformaInvoicesColumnsProps {
  isAdmin: boolean;
  isSalesExpert: boolean;
  isSalesManager: boolean;
  getSalesExpertName: (
    invoice: Pick<ProformaInvoice, "sales_expert_username" | "sales_expert_fullname">
  ) => string;
  handleViewSpecs: (invoice: ProformaInvoice) => void;
  handleViewPayments: (id: number) => void;
  handleEdit: (invoice: ProformaInvoice) => void;
  handleSalesManagerApprove: (id: number) => void;
  handleUnapproveSalesManager: (id: number) => void;
  handleCeoApprove: (id: number) => void;
  handleUnapproveCeo: (id: number) => void;
  setPaymentForm: (value: any) => void;
  resetPaymentForm: () => any;
  setShowRegisterPayment: (id: number | null) => void;
  setDeleteTargetId: (id: number | null) => void;
  /** باز کردن نمای چاپ برای این پیش‌فاکتور */
  handlePrint: (invoice: ProformaInvoice) => void;
  /** درج ستون «تاریخ» بعد از «کارشناس فروش» (صفحه‌های همه/سایر سفارش‌ها). */
  showDateColumn?: boolean;
  /**
   * حالت فقط‌خواندنی («سایر سفارش‌ها»). ستون‌های «عملیات» و «چاپ» ساخته نمی‌شوند؛
   * print-data فقط برای سفارش‌های خودِ کاربر مجاز است و برای سفارش کارشناس دیگر
   * خطای دسترسی می‌دهد.
   */
  readOnly?: boolean;
  /**
   * آیا کاربر جاری اجازه‌ی ثبت پرداخت دارد (کارشناس فروش، ادمین، سوپرادمین).
   * طبق بند ۸ مستند نسخه ۵ به وضعیت سفارش وابسته نیست.
   */
  mayRegisterPayment: boolean;
  /** آیا کاربر جاری اجازه‌ی تأیید پورسانت دارد (مدیر فروش، ادمین، سوپرادمین). */
  mayApproveCommission: boolean;
  /** شناسه‌ی سفارشی که همین لحظه درخواست پورسانتش در حال ارسال است. */
  commissionBusyId: number | null;
  handleApproveCommission: (invoice: ProformaInvoice) => void;
  handleUnapproveCommission: (id: number) => void;
}

export const getProformaInvoicesColumns = ({
  isAdmin,
  isSalesExpert,
  isSalesManager,
  getSalesExpertName,
  handleViewSpecs,
  handleViewPayments,
  handleEdit,
  handleSalesManagerApprove,
  handleUnapproveSalesManager,
  setPaymentForm,
  resetPaymentForm,
  setShowRegisterPayment,
  setDeleteTargetId,
  handlePrint,
  showDateColumn = false,
  readOnly = false,
  mayRegisterPayment,
  mayApproveCommission,
  commissionBusyId,
  handleApproveCommission,
  handleUnapproveCommission,
}: ProformaInvoicesColumnsProps) => {
  // ===== ستون‌های مشترک همه‌ی بخش‌ها =====
  const columns: any[] = [
    ...getOrderColumns({
      getSalesExpertName,
      onViewSpecs: handleViewSpecs,
      onViewPayments: (invoice: ProformaInvoice) => handleViewPayments(invoice.id),
      onRegisterPayment: (invoice: ProformaInvoice) => {
        setPaymentForm(resetPaymentForm());
        setShowRegisterPayment(invoice.id);
      },
      // بند ۸: مستقل از وضعیت سفارش؛ فقط سفارش تسویه‌شده دکمه را می‌بندد.
      canRegisterPayment: (invoice: ProformaInvoice) =>
        mayRegisterPayment && !isSettled(invoice),
      showDateColumn,

      // ===== سلول «تأیید پورسانت» =====
      // پیش از تأیید: دکمه‌ی «تأیید مدیر فروش». پس از تأیید: متن «تأیید شده» به
      // همراه نام تأییدکننده و تاریخ و ساعت. برداشتن تأیید فقط برای ادمین.
      renderCommissionApproval: (invoice: ProformaInvoice) => {
        const busy = commissionBusyId === invoice.id;
        if (invoice.commission_approved) {
          const by =
            (invoice.commission_approved_by_fullname || "").trim() ||
            invoice.commission_approved_by_username ||
            "";
          return (
            <div className="flex flex-col items-start gap-0.5">
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
                <CheckCircle2 className="size-4 shrink-0" />
                <span>تأیید شده{by ? ` توسط ${by}` : ""}</span>
              </span>
              {invoice.commission_approved_at_jalali && (
                <span className="text-[11px] text-slate-500 dark:text-zink-300 whitespace-nowrap">
                  {ltrValue(String(invoice.commission_approved_at_jalali))}
                </span>
              )}
              {/* در «سایر سفارش‌ها» این کنترل فقط نمایشی است و هیچ درخواستی نمی‌زند. */}
              {!readOnly && isAdmin && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => handleUnapproveCommission(invoice.id)}
                  className="text-[11px] text-orange-500 underline hover:text-orange-700 disabled:opacity-60"
                >
                  لغو تأیید
                </button>
              )}
            </div>
          );
        }

        // پیش‌نیاز تأیید: رابط و درصد ثبت شده باشند. مقدار صفر معتبر است.
        const hasCommission =
          Boolean(invoice.contact_id) &&
          invoice.commission_percentage !== null &&
          invoice.commission_percentage !== undefined &&
          String(invoice.commission_percentage).trim() !== "";
        if (readOnly || !mayApproveCommission || !hasCommission) {
          return <span className="text-blue-800 text-xs">—</span>;
        }
        return (
          <button
            type="button"
            disabled={busy}
            onClick={() => handleApproveCommission(invoice)}
            className={`${tableButtonClass} border-purple-500 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20 disabled:opacity-60`}
            title="تأیید پورسانت"
          >
            <CheckCircle2 className="size-4" />
            <span>تأیید پورسانت</span>
          </button>
        );
      },

      // فقط این صفحه دکمه‌های تأیید/لغو تأیید مدیر فروش را دارد.
      renderSalesManagerApproval: (invoice: ProformaInvoice) => {
        if (invoice.sales_manager_approved) {
          // نام تأییدکننده: اول نام کامل، بعد نام کاربری. برای تأییدهای ثبت‌شده پیش از
          // نسخه ۳ هیچ‌کدام موجود نیست و فقط «تأیید شده» نمایش داده می‌شود.
          const approvedBy =
            invoice.sales_manager_approved_by_fullname ||
            invoice.sales_manager_approved_by_username;
          const approvedAt = invoice.sales_manager_approved_at_jalali;
          // بازگشت فقط تا پیش از شروع تولید مجاز است؛ بعد از آن سرور با ۴۰۹ رد می‌کند.
          const beforeProduction =
            invoice.order_status === "proforma" || invoice.order_status === "sales_order";
          return (
            <div className="flex flex-col items-start gap-1">
              {/* قالب مستند: «تأیید شده توسط {نام}، {تاریخ و زمان}».
                  زمان با قفل جهت می‌آید تا در متن راست‌به‌چپ جابه‌جا نشود. */}
              <span className="inline-flex items-start gap-1 text-green-600 text-xs font-medium">
                <CheckCircle2 className="size-4 shrink-0 mt-0.5" />
                <span>
                  تأیید شده
                  {approvedBy ? ` توسط ${approvedBy}` : ""}
                  {approvedAt ? `، ${ltrValue(String(approvedAt))}` : ""}
                </span>
              </span>
              {/* «بازگشت» سفارش را به وضعیت تأییدنشده‌ی مدیر فروش برمی‌گرداند و اگر
                  سفارش فقط به‌خاطر همین تأیید به «سفارش فروش» رفته باشد، دوباره
                  «پیش‌فاکتور» می‌شود. فقط ادمین/سوپرادمین و فقط پیش از شروع تولید. */}
              {isAdmin && beforeProduction && (
                <button
                  type="button"
                  onClick={() => {
                    Swal.fire({
                      title: "بازگشت تأیید مدیر فروش",
                      text: `آیا این سفارش به وضعیت «تأییدنشده توسط مدیر فروش» بازگردانده شود؟ مشتری: ${invoice.customer_name || "-"}`,
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonText: "بله، بازگردان",
                      cancelButtonText: "خیر",
                    }).then((result) => {
                      if (result.isConfirmed) {
                        handleUnapproveSalesManager(invoice.id);
                      }
                    });
                  }}
                  className={`${tableButtonClass} border-orange-500 text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20`}
                  title="بازگشت به وضعیت تأییدنشده"
                >
                  <Undo2 className="size-4" />
                  <span>بازگشت</span>
                </button>
              )}
            </div>
          );
        }
        const canApprove = (isSalesManager || isAdmin) && invoice.order_status === "proforma";
        return canApprove ? (
          <button
            type="button"
            onClick={() => {
              const net = Number(invoice.net_amount || 0);
              const percentage =
                net > 0
                  ? Math.round((Number(invoice.confirmed_deposited_amount || 0) / net) * 100)
                  : 0;
              Swal.fire({
                title: "تأیید مدیر فروش",
                text: `مشتری ${invoice.customer_name || "-"} از کل مبلغ ${percentage}٪ را پرداخت کرده است. آیا ارسال این سفارش برای تولید را تایید می‌کنید؟`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "بله",
                cancelButtonText: "خیر",
              }).then((result) => {
                if (result.isConfirmed) {
                  handleSalesManagerApprove(invoice.id);
                }
              });
            }}
            className="inline-flex items-center gap-1 text-purple-500 hover:text-purple-700"
            title="تأیید مدیر فروش"
          >
            <CheckCircle2 className="size-4" />
            <span>تأیید مدیر فروش</span>
          </button>
        ) : (
          <span className="text-blue-800 text-xs">—</span>
        );
      },
    }),

    // ===== عملیات (مخصوص این صفحه) =====
    // در حالت فقط‌خواندنی این ستون اصلاً ساخته نمی‌شود.
    ...(readOnly ? [] : [{
      header: "عملیات",
      enableColumnFilter: false,
      exportable: false,
      cell: (cell: any) => {
        const invoice: ProformaInvoice = cell.row.original;
        // ادمین/سوپرادمین در هر وضعیتی؛ کارشناس فروش فقط در وضعیت پیش‌فاکتور و بدون واریزی تأییدشده.
        const canEditDeleteInvoice = isAdmin || (isSalesExpert && canSalesExpertModify(invoice));
        if (!canEditDeleteInvoice) return <span className="text-blue-800 text-xs">—</span>;
        return (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleEdit(invoice)}
              className={`${tableButtonClass} border-yellow-500 text-yellow-600 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-900/20`}
              title="ویرایش"
            >
              <Pencil className="size-4" />
              <span>ویرایش</span>
            </button>
            <button
              type="button"
              onClick={() => setDeleteTargetId(invoice.id)}
              className={`${tableButtonClass} border-red-500 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20`}
              title="حذف"
            >
              <Trash2 className="size-4" />
              <span>حذف</span>
            </button>
          </div>
        );
      },
    }]),
  ];

  // ستون «چاپ» بلافاصله بعد از «شماره فاکتور» می‌نشیند. جای دقیقش از روی همان
  // ستون پیدا می‌شود تا با اضافه‌شدن ستون «تاریخ» جابه‌جا نشود.
  // چاپ فقط‌خواندنی است، پس برای همه‌ی نقش‌هایی که این صفحه را می‌بینند فعال است.
  const invoiceNumberIndex = columns.findIndex((c) => c.header === "شماره فاکتور");
  if (!readOnly) columns.splice(invoiceNumberIndex === -1 ? 2 : invoiceNumberIndex + 1, 0, {
    header: "چاپ",
    enableColumnFilter: false,
    exportable: false,
    cell: (cell: any) => (
      <button
        type="button"
        onClick={() => handlePrint(cell.row.original)}
        className={`${tableButtonClass} border-slate-400 text-slate-600 hover:bg-slate-100 dark:text-zink-100 dark:hover:bg-zink-600`}
        title="چاپ پیش‌فاکتور"
      >
        <Printer className="size-4" />
        <span>چاپ</span>
      </button>
    ),
  });

  return columns;
};
// import { CheckCircle2, Pencil, Printer, Trash2 } from "lucide-react";
// import Swal from "sweetalert2";
// import type { ProformaInvoice } from "./index";
// import { canSalesExpertModify } from "./index";
// import { getOrderColumns, tableButtonClass } from "Common/Components/orderColumns";

// export interface ProformaInvoicesColumnsProps {
//   isAdmin: boolean;
//   isSalesExpert: boolean;
//   isSalesManager: boolean;
//   getSalesExpertName: (
//     invoice: Pick<ProformaInvoice, "sales_expert_username" | "sales_expert_fullname">
//   ) => string;
//   handleViewSpecs: (invoice: ProformaInvoice) => void;
//   handleViewPayments: (id: number) => void;
//   handleEdit: (invoice: ProformaInvoice) => void;
//   handleSalesManagerApprove: (id: number) => void;
//   handleUnapproveSalesManager: (id: number) => void;
//   handleCeoApprove: (id: number) => void;
//   handleUnapproveCeo: (id: number) => void;
//   setPaymentForm: (value: any) => void;
//   resetPaymentForm: () => any;
//   setShowRegisterPayment: (id: number | null) => void;
//   setDeleteTargetId: (id: number | null) => void;
//   /** باز کردن نمای چاپ برای این پیش‌فاکتور */
//   handlePrint: (invoice: ProformaInvoice) => void;
// }

// export const getProformaInvoicesColumns = ({
//   isAdmin,
//   isSalesExpert,
//   isSalesManager,
//   getSalesExpertName,
//   handleViewSpecs,
//   handleViewPayments,
//   handleEdit,
//   handleSalesManagerApprove,
//   handleUnapproveSalesManager,
//   setPaymentForm,
//   resetPaymentForm,
//   setShowRegisterPayment,
//   setDeleteTargetId,
//   handlePrint,
// }: ProformaInvoicesColumnsProps) => {
//   // ===== ستون‌های مشترک همه‌ی بخش‌ها =====
//   const columns: any[] = [
//     ...getOrderColumns({
//       getSalesExpertName,
//       onViewSpecs: handleViewSpecs,
//       onViewPayments: (invoice: ProformaInvoice) => handleViewPayments(invoice.id),
//       onRegisterPayment: (invoice: ProformaInvoice) => {
//         setPaymentForm(resetPaymentForm());
//         setShowRegisterPayment(invoice.id);
//       },
//       canRegisterPayment: (invoice: ProformaInvoice) =>
//         isAdmin || (isSalesExpert && invoice.order_status === "proforma"),

//       // فقط این صفحه دکمه‌های تأیید/لغو تأیید مدیر فروش را دارد.
//       renderSalesManagerApproval: (invoice: ProformaInvoice) => {
//         if (invoice.sales_manager_approved) {
//           return (
//             <div className="flex flex-col items-start gap-1">
//               <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
//                 <CheckCircle2 className="size-4" /> تأیید شده
//               </span>
//               {isAdmin && (
//                 <button
//                   type="button"
//                   onClick={() => handleUnapproveSalesManager(invoice.id)}
//                   className="text-xs text-orange-500 hover:text-orange-700 underline"
//                 >
//                   لغو تأیید
//                 </button>
//               )}
//             </div>
//           );
//         }
//         const canApprove = (isSalesManager || isAdmin) && invoice.order_status === "proforma";
//         return canApprove ? (
//           <button
//             type="button"
//             onClick={() => {
//               const net = Number(invoice.net_amount || 0);
//               const percentage =
//                 net > 0
//                   ? Math.round((Number(invoice.confirmed_deposited_amount || 0) / net) * 100)
//                   : 0;
//               Swal.fire({
//                 title: "تأیید مدیر فروش",
//                 text: `مشتری ${invoice.customer_name || "-"} از کل مبلغ ${percentage}٪ را پرداخت کرده است. آیا ارسال این سفارش برای تولید را تایید می‌کنید؟`,
//                 icon: "question",
//                 showCancelButton: true,
//                 confirmButtonText: "بله",
//                 cancelButtonText: "خیر",
//               }).then((result) => {
//                 if (result.isConfirmed) {
//                   handleSalesManagerApprove(invoice.id);
//                 }
//               });
//             }}
//             className="inline-flex items-center gap-1 text-purple-500 hover:text-purple-700"
//             title="تأیید مدیر فروش"
//           >
//             <CheckCircle2 className="size-4" />
//             <span>تأیید مدیر فروش</span>
//           </button>
//         ) : (
//           <span className="text-blue-800 text-xs">—</span>
//         );
//       },
//     }),

//     // ===== عملیات (مخصوص این صفحه) =====
//     {
//       header: "عملیات",
//       enableColumnFilter: false,
//       exportable: false,
//       cell: (cell: any) => {
//         const invoice: ProformaInvoice = cell.row.original;
//         // ادمین/سوپرادمین در هر وضعیتی؛ کارشناس فروش فقط در وضعیت پیش‌فاکتور و بدون واریزی تأییدشده.
//         const canEditDeleteInvoice = isAdmin || (isSalesExpert && canSalesExpertModify(invoice));
//         if (!canEditDeleteInvoice) return <span className="text-blue-800 text-xs">—</span>;
//         return (
//           <div className="flex flex-wrap gap-2">
//             <button
//               type="button"
//               onClick={() => handleEdit(invoice)}
//               className={`${tableButtonClass} border-yellow-500 text-yellow-600 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-900/20`}
//               title="ویرایش"
//             >
//               <Pencil className="size-4" />
//               <span>ویرایش</span>
//             </button>
//             <button
//               type="button"
//               onClick={() => setDeleteTargetId(invoice.id)}
//               className={`${tableButtonClass} border-red-500 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20`}
//               title="حذف"
//             >
//               <Trash2 className="size-4" />
//               <span>حذف</span>
//             </button>
//           </div>
//         );
//       },
//     },
//   ];

//   // ستون «چاپ» در جایگاه سوم می‌نشیند. چاپ فقط‌خواندنی است، پس برای همه‌ی
//   // نقش‌هایی که این صفحه را می‌بینند فعال است.
//   columns.splice(2, 0, {
//     header: "چاپ",
//     enableColumnFilter: false,
//     exportable: false,
//     cell: (cell: any) => (
//       <button
//         type="button"
//         onClick={() => handlePrint(cell.row.original)}
//         className={`${tableButtonClass} border-slate-400 text-slate-600 hover:bg-slate-100 dark:text-zink-100 dark:hover:bg-zink-600`}
//         title="چاپ پیش‌فاکتور"
//       >
//         <Printer className="size-4" />
//         <span>چاپ</span>
//       </button>
//     ),
//   });

//   return columns;
// };
